import * as React from "react"
import { Head, usePage } from "@inertiajs/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { MapPin } from "lucide-react"
import PublicLayout from "@/components/PublicLayout"
import { LocationMap } from "@/components/LocationMap"
import LocationCards, { type LocationCard } from "@/components/LocationCards"
import { HeroFilmSlides } from "@/components/gisela/HeroBand"
import { bandBg } from "@/components/gisela/TextBand"
import { useLocalePath } from "@/lib/locale-path"
import { sectionHidden } from "@/lib/section-visibility"
import type { PageMeta, PageProps } from "@/types/inertia"
import { BrandMotif } from "@/components/ui/brand-motif"

interface Photo {
  id: number
  url: string | null
  alt_text: string | null
}

interface Venue {
  id: number
  name: string
  slug: string
  region: string
  address_lines: string[]
  maps_url: string | null
  opening_hours: string | null
  latitude: number | null
  longitude: number | null
  href: string
  gallery_href: string
  /** Markdown, from the location's own Location & Getting Here section. */
  directions: string | null
  photo: Photo | null
}

interface Props {
  /** The home page's cards, one per location. */
  sites: LocationCard[]
  venues: Venue[]
  hero?: { slides: Photo[]; interval_seconds: number; film?: string | null }
  content: { hero: { heading?: string; subheading?: string; _hidden?: boolean } }
  meta?: PageMeta
}

function fill(template: string, values: Record<string, string>): string {
  return template.replace(/%\{(\w+)\}/g, (_, key) => values[key] ?? "")
}

/**
 * The "find us" page: every location with its address, map, opening hours
 * and directions — for the client or visiting therapist asking where you
 * are, as Rent a Room is for the practitioner asking what it costs to hire.
 * Each panel links to the location's own page; the closing band to Rent a
 * Room.
 */
export default function LocationsIndex({
  sites,
  venues,
  hero = { slides: [], interval_seconds: 6 },
  content,
  meta,
}: Props) {
  const { translations } = usePage<PageProps>().props
  const t = translations.sanctuary_pages.locations_page
  const tl = translations.sanctuary_pages.location
  const ts = translations.locations.show
  const lp = useLocalePath()
  const title = meta?.title ?? t.meta_title
  const description = meta?.description ?? t.meta_description
  const heroSlides = hero.slides.filter((s): s is Photo & { url: string } => !!s.url)
  // The cards take the first slot in the cream/stone alternation, so the
  // panels below them keep alternating rather than running two creams together.
  const bandOffset = sites.length > 0 ? 1 : 0

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <PublicLayout>
        {!sectionHidden(content.hero) && (
          <section className="relative overflow-hidden bg-teal pt-32 pb-16 text-white">
            <HeroFilmSlides slides={heroSlides} intervalMs={hero.interval_seconds * 1000} film={hero.film} />
            <BrandMotif variant="rings" tone="white" size={720} opacity={0.35} className="absolute -top-80 -left-64" />
            <div className="relative mx-auto max-w-4xl px-6 text-center lg:px-8">
              <p className="mb-3 text-xs font-medium uppercase tracking-widest text-white/60">{t.find_us}</p>
              <h1 className="text-white">{content.hero.heading || t.heading}</h1>
              <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/75">
                {content.hero.subheading || t.subheading}
              </p>
            </div>
          </section>
        )}

        {/* The same cards the home page opens with: the building's own
            photograph, its region and address, each to its page. They are the
            picture of the place the panels below deliberately don't carry —
            the panels give their right-hand side to the map. */}
        {sites.length > 0 && (
          <section className={`${bandBg(0)} py-16`}>
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <LocationCards sites={sites} />
            </div>
          </section>
        )}

        {/* One panel per location: the practical facts on the left, the map
            (or a placed photo) on the right, alternating band colours. */}
        {venues.map((venue, i) => (
          <section key={venue.id} id={venue.slug} className={`${bandBg(i + bandOffset)} py-16`}>
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
                <div className="space-y-6">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-widest text-ink-muted">{venue.region}</p>
                    <h2 className="mt-1">
                      <a href={lp(venue.href)}>{venue.name}</a>
                    </h2>
                    <p className="mt-3 flex items-start gap-1.5 text-ink-muted">
                      <MapPin className="mt-1 h-4 w-4 shrink-0" />
                      <span>
                        {venue.address_lines.map((line) => (
                          <React.Fragment key={line}>{line}<br /></React.Fragment>
                        ))}
                      </span>
                    </p>
                    {venue.maps_url && (
                      <a
                        href={venue.maps_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-block text-sm text-teal-mid hover:underline"
                      >
                        {ts.view_on_google_maps}
                      </a>
                    )}
                    {venue.opening_hours && (
                      <p className="mt-3 text-sm text-ink-muted">
                        {t.opening_hours}: {venue.opening_hours}
                      </p>
                    )}
                  </div>

                  {venue.directions && (
                    <div>
                      <h3>{t.getting_here}</h3>
                      <div className="mt-2 [&_p]:mt-2 [&_p:first-child]:mt-0">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{venue.directions}</ReactMarkdown>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <a href={lp(venue.href)} className="text-sm font-medium text-teal hover:underline">
                      {fill(t.view_rooms, { name: venue.name })}
                    </a>
                    <a href={lp(venue.gallery_href)} className="text-sm text-ink-muted hover:text-ink-body">
                      {fill(t.gallery, { name: venue.name })}
                    </a>
                  </div>
                </div>

                {venue.latitude != null && venue.longitude != null ? (
                  <LocationMap
                    latitude={venue.latitude}
                    longitude={venue.longitude}
                    name={venue.name}
                    mapsUrl={venue.maps_url}
                    mapLabel={tl.map_of}
                    directionsLabel={tl.directions}
                    className="lg:h-full"
                  />
                ) : venue.photo?.url ? (
                  <img
                    src={venue.photo.url}
                    alt={venue.photo.alt_text ?? venue.name}
                    className="h-72 w-full rounded-2xl object-cover ring-1 ring-hairline lg:h-auto"
                  />
                ) : (
                  <div className="flex h-72 items-center justify-center rounded-2xl bg-stone-mid ring-1 ring-hairline lg:h-auto">
                    <div className="text-center">
                      <MapPin className="mx-auto h-8 w-8 text-ink-muted" />
                      <p className="mt-2 text-sm text-ink-muted">{tl.map_coming_soon}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        ))}

        {/* The other question a visitor here may have. */}
        <section className={`relative overflow-hidden ${bandBg(venues.length + bandOffset)} border-t border-hairline py-16`}>
          <BrandMotif variant="disc" className="absolute -top-28 -right-36" />
          <div className="relative mx-auto max-w-3xl px-6 text-center lg:px-8">
            <h2>{t.hire_heading}</h2>
            <p className="mt-4 text-ink-muted">{t.hire_body}</p>
            <a
              href={lp("/rent-a-room")}
              className="mt-6 inline-block rounded-full bg-teal px-7 py-3 text-sm font-medium text-white no-underline transition-opacity hover:opacity-90"
            >
              {t.hire_cta}
            </a>
          </div>
        </section>
      </PublicLayout>
    </>
  )
}
